import { Assignment, RootState, Submission, SubmissionFile } from "@/types";
import { ExternalLink, FolderOpen, MessageSquare, Search } from "lucide-react";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useGetSubmissionsQuery } from "@/store/submissionApi";
import { useGetAssignmentsQuery } from "@/store/assignmentApi";
import { useSelector } from "react-redux";

const StudentSubmissions = () => {
  const { user } = useSelector((state: RootState) => state.user);
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [selectedAssignment, setSelectedAssignment] = React.useState<
    string | null
  >(null);

  //   Assignments
  const { data: assignment } = useGetAssignmentsQuery({
    batchCode: user?.batch._id,
    type: null,
    status: "",
  });

  //   Submissions
  const { data: submissions } = useGetSubmissionsQuery({
    assignmentId: selectedAssignment,
    studentId: user?._id,
  });

  //   Filtered Submissions
  const filteredSubmissions = submissions?.data.filter((submission) => {
    const search = searchTerm.toLowerCase();
    return (
      submission.assignmentId?.title?.toLowerCase().includes(search) ||
      submission.submissionFile?.some(
        (file) =>
          file.description?.toLowerCase().includes(search) ||
          file.source?.toLowerCase().includes(search)
      )
    );
  });

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="bg-gradient-to-br from-teal-50 via-teal-50 to-cyan-50 dark:from-[#49ccc1e2] dark:via-teal-950/20 dark:to-cyan-950/20 border-b border-gray-200/50 dark:border-gray-700/50 px-6 py-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <CardTitle className="flex items-center gap-2.5 text-gray-900 dark:text-gray-50 font-semibold text-lg">
            <div className="p-1.5 rounded-lg">
              <FolderOpen className="w-4 h-4 text-[#26BDB0] dark:text-[#127971]" />
            </div>
            My Submissions
          </CardTitle>
          <div className="flex flex-col sm:flex-row gap-2.5 w-full sm:w-auto">
            <Select
              onValueChange={(val) =>
                setSelectedAssignment(val === "all" ? null : val)
              }
              value={selectedAssignment || "all"}
            >
              <SelectTrigger className="w-full sm:w-[200px] h-9 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-sm">
                <SelectValue placeholder="Filter by Assignment" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assignments</SelectItem>
                {assignment?.data?.length ? (
                  assignment.data.map((item: Assignment) => (
                    <SelectItem key={item._id} value={item._id}>
                      {item.title}
                    </SelectItem>
                  ))
                ) : (
                  <SelectItem disabled value="none">
                    No assignments
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <div className="relative w-full sm:w-56">
              <Search className="absolute left-2.5 top-2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search..."
                className="pl-8 h-9 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className=" font-semibold border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/30">
                <th className="  text-left py-2.5 px-4  font-semibold text-gray-700 dark:text-gray-300 text-sm ">
                  Assignment
                </th>
                <th className="text-left py-2.5 px-4  font-semibold text-gray-700 dark:text-gray-300 text-sm  ">
                  Links
                </th>
                <th className="text-left py-2.5 px-4  font-semibold text-gray-700 dark:text-gray-300 text-sm  ">
                  Description
                </th>
                <th className="text-left py-2.5 px-4  font-semibold text-gray-700 dark:text-gray-300 text-sm  ">
                  Date
                </th>
                <th className="text-center py-2.5 px-4  font-semibold text-gray-700 dark:text-gray-300 text-sm  ">
                  Status
                </th>
                <th className="text-center py-2.5 px-4  font-semibold text-gray-700 dark:text-gray-300 text-sm  ">
                  Marks
                </th>
                <th className="text-left py-2.5 px-4  font-semibold text-gray-700 dark:text-gray-300 text-sm  ">
                  Feedback
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100 dark:divide-gray-800/50">
              {filteredSubmissions?.length ? (
                filteredSubmissions.map((submission: Submission) => (
                  <tr
                    key={submission._id}
                    className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30 transition-colors"
                  >
                    {/* Assignment Title */}
                    <td className="py-3 px-4">  
                      <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                        {submission.assignmentId?.title || "Untitled"}
                      </span>
                    </td>

                    {/* Project Links */}
                    <td className="py-3 px-4">
                      {submission.submissionFile?.length ? (
                        <div className="flex flex-col gap-1.5">
                          {submission.submissionFile.map(
                            (file: SubmissionFile, index: number) => (
                              <a
                                key={index}
                                href={file.source}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline text-xs font-medium"
                              >
                                {/* <Github className="w-3.5 h-3.5" /> */}
                                Project {index + 1}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 text-sm">
                          No links
                        </span>
                      )}
                    </td>

                    {/* Description */}
                    <td className="py-3 px-4 max-w-xs">
                      <span className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
                        {submission.submissionFile?.[0]?.description ||
                          "No description"}
                      </span>
                    </td>

                    {/* Submission Date */}
                    <td className="py-3 px-4">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {new Date(submission.submissionDate).toLocaleDateString(
                          "en-IN",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          }
                        )}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="py-3 px-4 text-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-semibold  tracking-wide ${
                          submission.status === "submitted"
                            ? "bg-teal-100 text-[#26BDB0] dark:bg-[#49ccc1e2] dark:text-[#127971]"
                            : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                        }`}
                      >
                        {submission.status}
                      </span>
                    </td>

                    {/* Marks */}
                    <td className="py-3 px-4 text-center">
                      <div className="inline-flex items-center rounded-md overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 transition-colors">
                        <span
                          className={`px-2.5 py-1 text-sm font-bold ${
                            submission.marksObtained !== null
                            ? "text-[#26BDB0] bg-teal-50 dark:text-[#127971] dark:bg-[#49ccc1e2]"
                              : "text-gray-500 bg-gray-50 dark:text-gray-400 dark:bg-gray-800"
                          }`}
                        >
                          {submission.marksObtained !== null
                            ? submission.marksObtained
                            : "—"}
                        </span>

                        <div className="w-px h-4 bg-gray-300 dark:bg-gray-600" />

                        <span className="px-2.5 py-1 text-sm font-bold text-red-600 bg-red-50 dark:text-red-300 dark:bg-red-900/30">
                          {submission.totalMarks}
                        </span>
                      </div>
                    </td>

                    {/* Feedback */}
                    <td className="py-3 px-4 relative group">
                      {submission?.feedback ? (
                        <>
                          <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                            <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="line-clamp-1">
                              {submission.feedback}
                            </span>
                          </div>
                          <div className="absolute z-50 hidden group-hover:block bg-gray-900 dark:bg-gray-800 text-white text-sm p-3 rounded-lg shadow-2xl w-64 left-0 top-full mt-1 border border-gray-700">
                            <div className="font-semibold mb-1.5 text-[#127971]">
                              Feedback:
                            </div>
                            <div className="text-gray-200 leading-relaxed">
                              {submission.feedback}
                            </div>
                          </div>
                        </>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500 text-sm">
                          No feedback
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center py-16">
                    <div className="flex flex-col items-center">
                      <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                        <FolderOpen
                          className="text-gray-400 dark:text-gray-500"
                          size={40}
                        />
                      </div>
                      <h3 className="text-base font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        No Submissions Found
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {searchTerm
                          ? "Try adjusting your search criteria"
                          : "You haven't submitted any assignments yet"}
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default StudentSubmissions;
